import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { DsqPwpProgram } from "../../../utils/dsqPayWithPoints/dsqPayWithPointsTypes";

/**
 * The DSQ webview session core opened for an order paid with partner points
 * (prepits/project#4468): what `/receipt/create` answered instead of a payment
 * link. The partner-points page reads it to load the webview and hand it the
 * token.
 *
 * Deliberately NOT persisted (the store registers it without
 * redux-persist): the access token is a merchant credential, so it lives in
 * memory only and never reaches storage. A reload drops it, and the page
 * sends the guest back to checkout.
 */
export type DsqpwpWebviewSession = {
  webviewUrl: string;
  accessToken: string;
  receiptId?: number;
  receiptUuid?: string;
};

type DsqPayWithPointsState = {
  session: DsqpwpWebviewSession | null;
  /**
   * Core sent the guest back to checkout because the partner points did not
   * pay the order (prepits/project#4439). Kept here, not in the card's hook,
   * so it outlives the checkout screen remounting after it was read; the
   * guest flipping the toggle, or a new webview session, clears it.
   */
  redemptionFailed: boolean;
  /**
   * The programs baked into the checkout at build time
   * (prepits/project#4516); null when the page was built without them. The
   * card reads them here and nothing fetches them in the browser.
   */
  bakedPrograms: DsqPwpProgram[] | null;
};

const initialState: DsqPayWithPointsState = {
  session: null,
  redemptionFailed: false,
  bakedPrograms: null,
};

const dsqPayWithPointsSlice = createSlice({
  name: "dsqPayWithPoints",
  initialState,
  reducers: {
    startDsqpwpSession: (
      state,
      { payload }: PayloadAction<DsqpwpWebviewSession>
    ) => {
      state.session = payload;
      state.redemptionFailed = false;
    },
    clearDsqpwpSession: (state) => {
      state.session = null;
    },
    markDsqpwpRedemptionFailed: (state) => {
      state.redemptionFailed = true;
    },
    clearDsqpwpRedemptionFailed: (state) => {
      state.redemptionFailed = false;
    },
    setBakedDsqpwpPrograms: (
      state,
      { payload }: PayloadAction<DsqPwpProgram[] | null>
    ) => {
      state.bakedPrograms = payload;
    },
  },
});

export const {
  startDsqpwpSession,
  clearDsqpwpSession,
  markDsqpwpRedemptionFailed,
  clearDsqpwpRedemptionFailed,
  setBakedDsqpwpPrograms,
} = dsqPayWithPointsSlice.actions;

export default dsqPayWithPointsSlice.reducer;
